import { serializeError } from 'serialize-error';
import DefaultTimeProvider from './datetime';
import type Logger from '../engine/Logger';

export interface RetryOpts {
  maxRetries?: number;
  minBackoffDelayMs?: number;
  maxBackoffDelayMs?: number;
  actionName?: string; // human-readable action name for debug purposes
  logger?: Logger;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abortOnError?: (error: any) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any) => Promise<void>;
}

const timeProvider = new DefaultTimeProvider();

const MAX_RETRIES_DEFAULT = 2;
const BACKOFF_DELAY_DEFAULT = 50;
const RETRY_DELAY_FACTOR = 2;

const retryAsync = async <ReturnType>(
  action: () => Promise<ReturnType>,
  opts: RetryOpts = {},
): Promise<ReturnType> => {
  const maxRetries = opts.maxRetries ?? MAX_RETRIES_DEFAULT;
  const minBackoffDelayMs = opts.minBackoffDelayMs ?? BACKOFF_DELAY_DEFAULT;
  let currentAttempt = 0;
  let lastError: Error | undefined = undefined;

  while (currentAttempt < maxRetries) {
    currentAttempt += 1;

    try {
      return await action();
    } catch (err: unknown) {
      lastError = err as Error;

      if (opts.logger) {
        opts.logger.info('Action retry failed', {
          currentAttempt,
          actionName: opts.actionName,
          error: serializeError(err),
        });
      }

      if (opts.abortOnError?.(err)) {
        throw lastError;
      }

      if (opts.onError) {
        await opts.onError(err);
      }

      if (currentAttempt < maxRetries) {
        const delay = RETRY_DELAY_FACTOR ** (currentAttempt - 1) * minBackoffDelayMs;

        await timeProvider.sleepMs(Math.min(delay, opts.maxBackoffDelayMs ?? delay));
      }
    }
  }

  throw lastError ?? new Error('retryAsync logic failed');
};

const retry = async <ReturnType extends () => ReturnType>(
  action: () => ReturnType,
  opts: RetryOpts = {},
// eslint-disable-next-line @typescript-eslint/require-await
): Promise<ReturnType> => await retryAsync(async () => action(), opts);

export { retryAsync };
export default retry;
